import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import LoginPage from './components/auth/LoginPage';
import AdminInvitationHandler from './components/auth/AdminInvitationHandler';
import ConsentGate from './components/consent/ConsentGate';
import Sidebar from './components/layout/Sidebar';
import Dashboard from './components/dashboard/Dashboard';
import UserManagement from './components/users/UserManagement';
import Settings from './components/settings/Settings';
import OrganizationManagement from './components/organizations/OrganizationManagement';
import ToastContainer from './components/common/ToastContainer';
import './index.css';

// Initialize dark mode on app load — runs once before any component renders
function initDarkMode() {
  const stored = localStorage.getItem('theme');
  if (stored === 'dark' || (!stored && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
}
initDarkMode();

const AdminLayout: React.FC = () => {
  const { currentUser, loading } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('adminSidebarCollapsed');
    return saved === 'true';
  });

  // Check if this is an admin invitation handler route
  const [isInvitationHandler, setIsInvitationHandler] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hasEmailParam = urlParams.has('email');
    const modeParam = urlParams.get('mode');
    const isSignInLink = window.location.href.includes('apiKey') || window.location.href.includes('oobCode');
    
    // Check if this is an admin invitation (has email param and either mode=signIn or Firebase auth params)
    if (hasEmailParam && (modeParam === 'signIn' || isSignInLink)) {
      setIsInvitationHandler(true);
    }
  }, []);

  // Listen for sidebar collapse changes - MUST be before any conditional returns
  React.useEffect(() => {
    const handleSidebarChange = () => {
      const saved = localStorage.getItem('adminSidebarCollapsed');
      setSidebarCollapsed(saved === 'true');
    };
    window.addEventListener('sidebarToggle', handleSidebarChange);
    return () => window.removeEventListener('sidebarToggle', handleSidebarChange);
  }, []);

  // Show invitation handler if this is an invitation link
  if (isInvitationHandler) {
    return <AdminInvitationHandler />;
  }

  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!currentUser) {
    return <LoginPage />;
  }

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setMobileMenuOpen(false);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <Dashboard />;
      case 'organizations':
        return <OrganizationManagement />;
      case 'users':
        return <UserManagement />;
      case 'sessions':
        return (
          <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 dark:bg-gray-900 min-h-screen">
            <div className="mb-6 sm:mb-8">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Sessions</h1>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1 sm:mt-2">Session management coming soon...</p>
            </div>
          </div>
        );
      case 'settings':
        return <Settings />;
      default:
        return <Dashboard />;
    }
  };

  return (
    <ConsentGate>
      <div className="h-screen w-full bg-gray-50 dark:bg-gray-900 flex">
        <Sidebar activeTab={activeTab} onTabChange={handleTabChange} mobileOpen={mobileMenuOpen} onMobileClose={() => setMobileMenuOpen(false)} />
        <main className={`transition-all duration-300 flex-1 overflow-auto ${sidebarCollapsed ? 'lg:pl-[60px]' : 'lg:pl-60'}`}>
          {/* Mobile Header */}
          <div className="lg:hidden sticky top-0 z-30 flex items-center gap-3 px-4 py-3 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="p-2 rounded-xl text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Open menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <img src="/icon-virtualy.png" alt="Virtual Client" className="h-7 w-7" />
            <span className="font-semibold text-gray-900 dark:text-white text-sm">Virtual Client Admin</span>
          </div>
          {renderContent()}
        </main>
        <ToastContainer />
      </div>
    </ConsentGate>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <ToastProvider>
        <AdminLayout />
      </ToastProvider>
    </AuthProvider>
  );
};

export default App;